import { FC, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createFeeClassRequest,
	resetSaveFeeClassSuccess,
	updateFeeClassRequest,
} from 'redux/reducers/easFee/reducer';
import { getFeeClassSaveLoading, getFeeClassSaveSuccess } from 'redux/reducers/easFee/selectors';
import { FEE_CLASS_NUMBER_FIELDS, FIELD_STEP } from './constants';
import { EFeeClassFieldKind, TFeeClassDialogProps, TFeeClassFormValues } from './types';

const buildValidationSchema = () =>
	Yup.object({
		name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
		...Object.fromEntries(
			FEE_CLASS_NUMBER_FIELDS.map((field) => {
				let rule = Yup.number().required(`${field.label} is required`).min(0, 'Must be >= 0');
				if (field.kind === EFeeClassFieldKind.PERCENT) {
					rule = rule.max(100, 'Must be <= 100');
				}
				if (field.kind === EFeeClassFieldKind.COUNT) {
					rule = rule.integer('Must be an integer');
				}
				return [field.name, rule];
			}),
		),
	});

export const FeeClassDialog: FC<TFeeClassDialogProps> = ({ open, onClose, feeClassToEdit }) => {
	const dispatch = useDispatch();
	const isEdit = !!feeClassToEdit;

	const loading = useSelector(getFeeClassSaveLoading);
	const saveSuccess = useSelector(getFeeClassSaveSuccess);

	const validationSchema = useMemo(buildValidationSchema, []);

	const formik = useFormik<TFeeClassFormValues>({
		initialValues: {
			name: feeClassToEdit?.name ?? '',
			...(Object.fromEntries(
				FEE_CLASS_NUMBER_FIELDS.map((field) => [
					field.name,
					feeClassToEdit?.[field.name] ?? ('' as unknown as number),
				]),
			) as Omit<TFeeClassFormValues, 'name'>),
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name,
				...(Object.fromEntries(
					FEE_CLASS_NUMBER_FIELDS.map((field) => [field.name, Number(values[field.name])]),
				) as Omit<TFeeClassFormValues, 'name'>),
			};
			if (isEdit && feeClassToEdit) {
				dispatch(updateFeeClassRequest({ id: feeClassToEdit.id, body }));
			} else {
				dispatch(createFeeClassRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveFeeClassSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Fee Class' : 'Add New Fee Class'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
							gap: 2,
						}}
					>
						{FEE_CLASS_NUMBER_FIELDS.map((field) => (
							<TextField
								key={field.name}
								label={field.label}
								name={field.name}
								type="number"
								size="small"
								inputProps={{ step: FIELD_STEP[field.kind] }}
								value={formik.values[field.name]}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched[field.name] && !!formik.errors[field.name]}
								helperText={formik.touched[field.name] && formik.errors[field.name]}
							/>
						))}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
